import React from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { FileText, Presentation, Download, Image } from 'lucide-react';

const DownloadsPage = () => {
  const downloads = [
    {
      title: "Foundation Letterhead",
      description: "Official letterhead template for correspondence. Features the foundation's branding with dark header and amber accents.",
      filename: "NimoyKnight_Letterhead.pdf",
      icon: FileText,
      type: "PDF Document",
      size: "~50 KB"
    },
    {
      title: "Presentation Template",
      description: "PowerPoint template with 4 slide layouts: Title, Content, Quote, and Closing slides. Dark theme with amber accents matching the website.",
      filename: "NimoyKnight_Template.pptx",
      icon: Presentation,
      type: "PowerPoint",
      size: "~100 KB"
    }
  ];

  const logoDownloads = [
    {
      title: "Logo (with White Background)",
      description: "High-resolution foundation logo with white circular background. Perfect for print materials and light backgrounds.",
      url: "https://static.prod-images.emergentagent.com/jobs/4deff940-82ec-418a-802b-27f3b8967809/images/ee733fa2b8ee14d71baab2df68d023557ef19769e0079d117ba5cb5241cf92cb.png",
      filename: "NimoyKnight_Logo_WhiteBG.png",
      type: "PNG Image",
      size: "~750 KB"
    },
    {
      title: "Logo (Transparent Background)",
      description: "High-resolution foundation logo with transparent background. Ideal for overlays, dark backgrounds, and digital use.",
      url: "https://static.prod-images.emergentagent.com/jobs/ad0eba10-a6d6-4168-9a61-2b53a17441e4/images/333eaf070e3b703b60c0e57716965114398c5c169ff406d6be87758a1dfdcef9.png",
      filename: "NimoyKnight_Logo_Transparent.png",
      type: "PNG Image",
      size: "~500 KB"
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0b]">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-transparent to-transparent" />
        
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-5xl mb-6">📁</div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="text-white">Brand </span>
            <span className="text-amber-500">Resources</span>
          </h1>
          <p className="text-gray-300 text-lg max-w-2xl mx-auto">
            Download official Nimoy-Knight Foundation templates for your presentations and correspondence.
          </p>
        </div>
      </section>

      {/* Downloads Grid */}
      <section className="py-16 bg-[#0a0a0b]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Logo Downloads */}
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <Image className="w-6 h-6 text-amber-500" />
              Logo Files
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {logoDownloads.map((item, index) => (
                <div
                  key={index}
                  className="bg-[#141416] border border-gray-800/50 rounded-2xl p-6 hover:border-amber-500/30 transition-all"
                >
                  {/* Logo Preview */}
                  <div className="w-full h-32 bg-[#0a0a0b] rounded-xl flex items-center justify-center mb-4 overflow-hidden">
                    <img 
                      src={item.url} 
                      alt={item.title} 
                      className="h-28 w-auto object-contain"
                    />
                  </div>
                  
                  {/* Content */}
                  <h3 className="text-lg font-bold text-white mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-400 text-sm mb-3 leading-relaxed">
                    {item.description}
                  </p>
                  
                  {/* File Info */}
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                    <span>{item.type}</span>
                    <span>•</span>
                    <span>{item.size}</span>
                  </div>
                  
                  {/* Download Button */}
                  <a
                    href={item.url}
                    download={item.filename}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-full transition-all hover:scale-105 text-sm"
                  >
                    <Download className="w-4 h-4" />
                    Download PNG
                  </a>
                </div>
              ))}
            </div>
          </div>

          {/* Templates */}
          <div>
            <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
              <FileText className="w-6 h-6 text-amber-500" />
              Templates
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {downloads.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <div
                    key={index}
                    className="bg-[#141416] border border-gray-800/50 rounded-2xl p-8 hover:border-amber-500/30 transition-all"
                  >
                    {/* Icon */}
                    <div className="w-16 h-16 bg-amber-500/10 rounded-2xl flex items-center justify-center mb-6">
                      <IconComponent className="w-8 h-8 text-amber-500" />
                    </div>
                    
                    {/* Content */}
                    <h3 className="text-xl font-bold text-white mb-2">
                      {item.title}
                    </h3>
                    <p className="text-gray-400 text-sm mb-4 leading-relaxed">
                      {item.description}
                    </p>
                    
                    {/* File Info */}
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-6">
                      <span>{item.type}</span>
                      <span>•</span>
                      <span>{item.size}</span>
                    </div>
                    
                    {/* Download Button */}
                    <a
                      href={`/downloads/${item.filename}`}
                      download={item.filename}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-full transition-all hover:scale-105"
                      data-testid={`download-${item.filename}`}
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </a>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Usage Guidelines */}
      <section className="py-16 bg-[#0f0f12]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-amber-500 text-sm font-semibold tracking-widest uppercase">
              Guidelines
            </span>
            <h2 className="text-3xl font-bold text-white mt-4">
              Using Our Brand Resources
            </h2>
          </div>
          
          <div className="bg-[#141416] border border-gray-800/50 rounded-2xl p-8">
            <ul className="space-y-4 text-gray-300">
              <li className="flex items-start gap-3">
                <span className="text-amber-500 mt-1">•</span>
                <span>These templates are provided for official Nimoy-Knight Foundation communications and presentations.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-500 mt-1">•</span>
                <span>Please maintain the integrity of the foundation's visual identity when using these templates.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-500 mt-1">•</span>
                <span>The letterhead template is designed to be printed on standard letter-size paper (8.5" x 11").</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-500 mt-1">•</span>
                <span>The PowerPoint template uses a 16:9 widescreen aspect ratio for optimal presentation display.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-amber-500 mt-1">•</span>
                <span>For questions about brand usage, please contact us at info@rememberingleonardnimoy.org</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* LLAP Section */}
      <section className="py-16 bg-[#0a0a0b]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-4xl mb-4">🖖</div>
          <p className="text-amber-500 font-semibold text-lg">
            Live Long and Prosper
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DownloadsPage;
